import { FeatureUsageType } from "@autumn/shared";
import { cn } from "@/lib/utils";

export interface FeatureBalanceDisplayProps {
	allowance: number;
	balance: number;
	rolloverBalance?: number;
	shouldShowOutOfBalance: boolean;
	shouldShowUsed: boolean;
	usageType?: string;
	className?: string;
	initialAllowance: number;
	compact?: boolean;
}

/**
 * Shared display component for feature balance/usage numbers
 */
export function FeatureBalanceDisplay({
	allowance,
	balance,
	rolloverBalance = 0,
	shouldShowOutOfBalance,
	shouldShowUsed,
	usageType,
	className,
	initialAllowance,
	compact = false,
}: FeatureBalanceDisplayProps) {
	const formatNumber = (num: number) => new Intl.NumberFormat().format(num);
	const displayBalance = balance < 0 ? 0 : balance;
	const overage = balance < 0 ? balance * -1 : 0;

	const getUsedLabel = () => {
		if (initialAllowance > 0) return "overage";
		if (usageType === FeatureUsageType.Continuous) return "in use";
		return "used";
	};

	return (
		<div className={cn("flex items-baseline gap-1 truncate", className)}>
			{shouldShowOutOfBalance && (
				<>
					<span className="text-foreground">{formatNumber(displayBalance)}</span>
					{allowance > 0 && (
						<span className="text-subtle">
							{compact
								? `/ ${formatNumber(allowance)}`
								: `/ ${formatNumber(allowance)} left`}
						</span>
					)}
					{rolloverBalance > 0 && (
						<span className="text-subtle">
							(+{formatNumber(rolloverBalance)} rollover)
						</span>
					)}
				</>
			)}
			{shouldShowUsed && (
				<span className="truncate">
					{shouldShowOutOfBalance && shouldShowUsed && " +"}
					{formatNumber(overage)}{" "}
					<span className="text-subtle truncate">{getUsedLabel()}</span>
				</span>
			)}
		</div>
	);
}
